import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Plus, Minus, Trash2, Copy, Download, Clock } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  durationBetween,
  shiftTime,
  sumDurations,
  calcWage,
  convertUnit,
  format12h,
  format24h,
  formatHMS,
  type TimeParts,
  type TimeUnit,
} from "@/lib/calculators/timeCalculator";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type TabId = "duration" | "addsub" | "timesheet" | "convert";

const T = {
  h1: { en: "Time Calculator", bn: "টাইম ক্যালকুলেটর" },
  desc: {
    en: "Calculate time duration, add or subtract time, sum timesheet hours, and convert time units.",
    bn: "সময়ের ব্যবধান, সময় যোগ/বিয়োগ, টাইমশিট ঘণ্টা যোগফল, এবং সময়ের একক রূপান্তর।",
  },
  tDur: { en: "Duration", bn: "সময়ের ব্যবধান" },
  tAddSub: { en: "Add / Subtract", bn: "যোগ / বিয়োগ" },
  tSheet: { en: "Timesheet", bn: "টাইমশিট" },
  tConv: { en: "Unit converter", bn: "একক রূপান্তর" },

  start: { en: "Start time", bn: "শুরুর সময়" },
  end: { en: "End time", bn: "শেষের সময়" },
  startDate: { en: "Start date (optional)", bn: "শুরুর তারিখ (ঐচ্ছিক)" },
  endDate: { en: "End date (optional)", bn: "শেষের তারিখ (ঐচ্ছিক)" },
  hours: { en: "Hours", bn: "ঘণ্টা" },
  minutes: { en: "Minutes", bn: "মিনিট" },
  seconds: { en: "Seconds", bn: "সেকেন্ড" },
  days: { en: "Days", bn: "দিন" },
  weeks: { en: "Weeks", bn: "সপ্তাহ" },
  add: { en: "Add", bn: "যোগ" },
  sub: { en: "Subtract", bn: "বিয়োগ" },
  result: { en: "Result", bn: "ফলাফল" },
  total: { en: "Total", bn: "মোট" },
  addRow: { en: "Add row", bn: "সারি যোগ" },
  rate: { en: "Hourly rate (৳)", bn: "ঘণ্টা প্রতি মজুরি (৳)" },
  wage: { en: "Total wage", bn: "মোট মজুরি" },
  exportCsv: { en: "Export CSV", bn: "CSV রপ্তানি" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  use24: { en: "24-hour", bn: "২৪-ঘণ্টা" },
  label: { en: "Label", bn: "লেবেল" },
  duration: { en: "Duration", bn: "ব্যবধান" },
  value: { en: "Value", bn: "মান" },
  unit: { en: "Unit", bn: "একক" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How to calculate hours between two times?", bn: "দুই সময়ের মধ্যে ঘণ্টা কীভাবে?" },
    a: {
      en: "Convert both times to seconds since midnight, subtract, then format. If end is before start (overnight shift), add 24 hours. Example: 9:30 AM to 5:45 PM = 8h 15m.",
      bn: "দুই সময়কে রাত ১২টা থেকে সেকেন্ডে রূপান্তর করে বিয়োগ করুন। শেষ সময় শুরুর আগে হলে (ওভারনাইট শিফট) ২৪ ঘণ্টা যোগ করুন। উদাহরণ: ৯:৩০ AM → ৫:৪৫ PM = ৮ ঘ ১৫ মি।",
    },
  },
  {
    q: { en: "How to add hours and minutes?", bn: "ঘণ্টা ও মিনিট কীভাবে যোগ?" },
    a: {
      en: "Convert everything to seconds, sum, then split into hours and minutes by dividing by 3600 and 60. If total exceeds 24h, the calculator shows '+1 day'.",
      bn: "সব সেকেন্ডে রূপান্তর করে যোগ করুন, পরে ৩৬০০ ও ৬০ দিয়ে ভাগ করে ঘণ্টা ও মিনিটে রূপান্তর। মোট ২৪ ঘণ্টা ছাড়ালে '+১ দিন' দেখাবে।",
    },
  },
  {
    q: { en: "টাইম কীভাবে যোগ করা হয়?", bn: "টাইম কীভাবে যোগ করা হয়?" },
    a: {
      en: "প্রতিটি অংশ (ঘণ্টা, মিনিট, সেকেন্ড) আলাদাভাবে যোগ করুন। ৬০ সেকেন্ড = ১ মিনিট, ৬০ মিনিট = ১ ঘণ্টা ক্যারি করুন। ক্যালকুলেটর স্বয়ংক্রিয়ভাবে এটি করে।",
      bn: "প্রতিটি অংশ (ঘণ্টা, মিনিট, সেকেন্ড) আলাদাভাবে যোগ করুন। ৬০ সেকেন্ড = ১ মিনিট, ৬০ মিনিট = ১ ঘণ্টা ক্যারি করুন। ক্যালকুলেটর স্বয়ংক্রিয়ভাবে এটি করে।",
    },
  },
  {
    q: { en: "How does cross-midnight calculation work?", bn: "ক্রস-মিডনাইট গণনা কীভাবে?" },
    a: {
      en: "If end time is earlier than start time (e.g., 11:30 PM → 2:30 AM), we assume the end is on the next day and add 24 hours, giving 3 hours. Use date fields for shifts spanning multiple days.",
      bn: "শেষ সময় শুরুর সময়ের চেয়ে কম হলে (যেমন ১১:৩০ PM → ২:৩০ AM) পরের দিন ধরে ২৪ ঘণ্টা যোগ করা হয় = ৩ ঘণ্টা। একাধিক দিন জুড়ে শিফট হলে তারিখ ফিল্ড ব্যবহার করুন।",
    },
  },
  {
    q: { en: "Can I save my timesheet?", bn: "আমার টাইমশিট কি সেভ হবে?" },
    a: {
      en: "Yes — your timesheet rows auto-save in your browser (localStorage) and persist across visits on the same device. Use 'Export CSV' to download for records.",
      bn: "হ্যাঁ — টাইমশিট সারি স্বয়ংক্রিয়ভাবে আপনার ব্রাউজারে (localStorage) সেভ হয় এবং একই ডিভাইসে পরের ভিজিটেও থাকে। রেকর্ডের জন্য 'CSV রপ্তানি' করুন।",
    },
  },
  {
    q: { en: "How many seconds in a day, week, month?", bn: "এক দিন, সপ্তাহ, মাসে কত সেকেন্ড?" },
    a: {
      en: "1 day = 86,400 sec; 1 week = 604,800 sec; 1 month (avg) ≈ 2,629,800 sec; 1 year (avg) ≈ 31,557,600 sec. Use the unit converter tab for any value.",
      bn: "১ দিন = ৮৬,৪০০ সে; ১ সপ্তাহ = ৬,০৪,৮০০ সে; ১ মাস (গড়) ≈ ২৬,২৯,৮০০ সে; ১ বছর (গড়) ≈ ৩,১৫,৫৭,৬০০ সে। যেকোনো মান একক রূপান্তর ট্যাবে দেখুন।",
    },
  },
];

export const Route = createFileRoute("/time-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Time Calculator — Duration, Add Time, Timesheet",
      description:
        "Free time calculator. Calculate time duration, add or subtract time, sum timesheet hours, convert time units. টাইম ক্যালকুলেটর।",
      path: "/time-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Time Calculator", path: "/time-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: TimeCalculatorPage,
});

const HASH_TO_TAB: Record<string, TabId> = {
  duration: "duration",
  addsub: "addsub",
  timesheet: "timesheet",
  convert: "convert",
};

const STORAGE_KEY = "bc.timesheet.v1";

interface SheetRow {
  id: string;
  label: string;
  h: string;
  m: string;
  s: string;
}

function newRow(label = ""): SheetRow {
  return { id: Math.random().toString(36).slice(2, 9), label, h: "0", m: "0", s: "0" };
}

function TimeCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const [use24, setUse24] = useState(false);

  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmtN = (n: number) => num(n.toLocaleString("en-US"));

  const [tab, setTab] = useState<TabId>("duration");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hash.replace("#", "");
    if (HASH_TO_TAB[h]) setTab(HASH_TO_TAB[h]);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.replace("#", "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  // Tab 1: duration
  const [startStr, setStartStr] = useState("09:30");
  const [endStr, setEndStr] = useState("17:45");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const startParts = parseTimeInput(startStr);
  const endParts = parseTimeInput(endStr);
  const dur = useMemo(() => {
    if (!startParts || !endParts) return null;
    const sd = startDate ? new Date(startDate) : null;
    const ed = endDate ? new Date(endDate) : null;
    return durationBetween(startParts, endParts, sd, ed);
  }, [startParts, endParts, startDate, endDate]);

  // Tab 2: add/subtract
  const [baseStr, setBaseStr] = useState("15:30");
  const [op, setOp] = useState<"add" | "sub">("add");
  const [dH, setDH] = useState("2");
  const [dM, setDM] = useState("45");
  const [dS, setDS] = useState("0");

  const addsubResult = useMemo(() => {
    const base = parseTimeInput(baseStr);
    if (!base) return null;
    const delta: TimeParts = {
      h: intOr(dH, 0),
      m: intOr(dM, 0),
      s: intOr(dS, 0),
    };
    return shiftTime(base, delta, op);
  }, [baseStr, op, dH, dM, dS]);

  // Tab 3: timesheet
  const [rows, setRows] = useState<SheetRow[]>([
    newRow("Mon"),
    newRow("Tue"),
    newRow("Wed"),
  ]);
  const [rate, setRate] = useState("200");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.rows) && parsed.rows.length) setRows(parsed.rows);
        if (typeof parsed.rate === "string") setRate(parsed.rate);
      }
    } catch {}
    setHydrated(true);
  }, []);
  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ rows, rate }));
    } catch {}
  }, [rows, rate, hydrated]);

  const sheetRows: TimeParts[] = rows.map((r) => ({
    h: intOr(r.h, 0),
    m: intOr(r.m, 0),
    s: intOr(r.s, 0),
  }));
  const totalSec = sumDurations(sheetRows);
  const wage = calcWage(totalSec, parseFloat(rate) || 0);

  // Tab 4: unit converter
  const [convVal, setConvVal] = useState("3");
  const [convUnit, setConvUnit] = useState<TimeUnit>("hours");
  const conv = useMemo(() => {
    const v = parseFloat(convVal);
    if (!Number.isFinite(v)) return null;
    return convertUnit(v, convUnit);
  }, [convVal, convUnit]);

  const fmtTime = (p: TimeParts) => (use24 ? format24h(p) : format12h(p));

  const exportCsv = () => {
    const header = "Label,Hours,Minutes,Seconds,Total Seconds\n";
    const body = rows
      .map((r) => {
        const sec = intOr(r.h, 0) * 3600 + intOr(r.m, 0) * 60 + intOr(r.s, 0);
        return `"${r.label}",${intOr(r.h, 0)},${intOr(r.m, 0)},${intOr(r.s, 0)},${sec}`;
      })
      .join("\n");
    const totalsRow = `\n"TOTAL",,,,${totalSec}`;
    const blob = new Blob([header + body + totalsRow], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "timesheet.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <CalculatorLayout
      slug="time-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Clock"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-4 text-xs text-muted-foreground">
        <label className={cn("inline-flex items-center gap-2", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={use24} onChange={(e) => setUse24(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("use24")}
        </label>
        <label className={cn("inline-flex items-center gap-2", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="duration" className={cn(lang === "bn" && "font-bn")}>{tr("tDur")}</TabsTrigger>
          <TabsTrigger value="addsub" className={cn(lang === "bn" && "font-bn")}>{tr("tAddSub")}</TabsTrigger>
          <TabsTrigger value="timesheet" className={cn(lang === "bn" && "font-bn")}>{tr("tSheet")}</TabsTrigger>
          <TabsTrigger value="convert" className={cn(lang === "bn" && "font-bn")}>{tr("tConv")}</TabsTrigger>
        </TabsList>

        {/* Tab 1: Duration */}
        <TabsContent value="duration" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <TimeField label={tr("start")} value={startStr} onChange={setStartStr} lang={lang} />
            <TimeField label={tr("end")} value={endStr} onChange={setEndStr} lang={lang} />
            <DateInput label={tr("startDate")} value={startDate} onChange={setStartDate} lang={lang} />
            <DateInput label={tr("endDate")} value={endDate} onChange={setEndDate} lang={lang} />
          </div>

          {dur && startParts && endParts && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                  {fmtTime(startParts)} → {fmtTime(endParts)}
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">
                  {fmtN(dur.h)} <span className="text-lg text-muted-foreground">{lang === "en" ? "h" : "ঘ"}</span>{" "}
                  {fmtN(dur.m)} <span className="text-lg text-muted-foreground">{lang === "en" ? "m" : "মি"}</span>
                  {dur.s > 0 && (
                    <> {fmtN(dur.s)} <span className="text-lg text-muted-foreground">{lang === "en" ? "s" : "সে"}</span></>
                  )}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat label={lang === "en" ? "Decimal hours" : "দশমিক ঘণ্টা"} value={num((dur.totalSeconds / 3600).toFixed(2))} />
                <Stat label={lang === "en" ? "Total minutes" : "মোট মিনিট"} value={fmtN(Math.floor(dur.totalSeconds / 60))} />
                <Stat label={lang === "en" ? "Total seconds" : "মোট সেকেন্ড"} value={fmtN(dur.totalSeconds)} />
              </div>
            </motion.div>
          )}
        </TabsContent>

        {/* Tab 2: Add / Subtract */}
        <TabsContent value="addsub" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <TimeField label={lang === "en" ? "Starting time" : "শুরুর সময়"} value={baseStr} onChange={setBaseStr} lang={lang} />
            <div className="space-y-1.5">
              <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
                {lang === "en" ? "Operation" : "অপারেশন"}
              </label>
              <div className="flex gap-2">
                {(["add", "sub"] as const).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOp(o)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium",
                      op === o ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card hover:border-accent/40",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {o === "add" ? <Plus size={12} /> : <Minus size={12} />}
                    {o === "add" ? tr("add") : tr("sub")}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <NumField label={tr("hours")} value={dH} onChange={setDH} lang={lang} useBn={useBn} />
            <NumField label={tr("minutes")} value={dM} onChange={setDM} lang={lang} useBn={useBn} />
            <NumField label={tr("seconds")} value={dS} onChange={setDS} lang={lang} useBn={useBn} />
          </div>

          {addsubResult && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
              <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("result")}
              </p>
              <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
                {fmtTime(addsubResult.result)}
              </p>
              {addsubResult.dayOffset !== 0 && (
                <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {addsubResult.dayOffset > 0
                    ? lang === "en"
                      ? `+${addsubResult.dayOffset} day${addsubResult.dayOffset > 1 ? "s" : ""}`
                      : `+${num(addsubResult.dayOffset)} দিন পরে`
                    : lang === "en"
                      ? `${addsubResult.dayOffset} day${addsubResult.dayOffset < -1 ? "s" : ""}`
                      : `${num(addsubResult.dayOffset)} দিন আগে`}
                </p>
              )}
            </motion.div>
          )}
        </TabsContent>

        {/* Tab 3: Timesheet */}
        <TabsContent value="timesheet" className="mt-6 space-y-4">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{tr("label")}</th>
                  <th className="p-2 text-right">{tr("hours")}</th>
                  <th className="p-2 text-right">{tr("minutes")}</th>
                  <th className="p-2 text-right">{tr("seconds")}</th>
                  <th className="p-2 text-right">{tr("duration")}</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const sec = intOr(r.h, 0) * 3600 + intOr(r.m, 0) * 60 + intOr(r.s, 0);
                  return (
                    <tr key={r.id} className="border-t border-border">
                      <td className="p-2">
                        <input
                          value={r.label}
                          onChange={(e) => updateRow(i, { label: e.target.value }, rows, setRows)}
                          placeholder={lang === "en" ? `Day ${i + 1}` : `দিন ${num(i + 1)}`}
                          className="w-24 rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-accent md:w-32"
                        />
                      </td>
                      {(["h", "m", "s"] as const).map((k) => (
                        <td key={k} className="p-2 text-right">
                          <input
                            inputMode="numeric"
                            value={useBn ? toBengaliNumerals(r[k]) : r[k]}
                            onChange={(e) =>
                              updateRow(
                                i,
                                {
                                  [k]: e.target.value
                                    .replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)))
                                    .replace(/[^0-9]/g, ""),
                                } as Partial<SheetRow>,
                                rows,
                                setRows,
                              )
                            }
                            className="w-16 rounded border border-border bg-background px-2 py-1 text-right tabular-nums outline-none focus:border-accent"
                          />
                        </td>
                      ))}
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{num(formatHMS(sec))}</td>
                      <td className="p-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() => setRows((rs) => [...rs.slice(0, i + 1), { ...r, id: Math.random().toString(36).slice(2, 9) }, ...rs.slice(i + 1)])}
                            className="rounded p-1 text-muted-foreground hover:text-accent"
                            aria-label="Duplicate"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                            className="rounded p-1 text-muted-foreground hover:text-destructive"
                            aria-label="Remove"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-accent/5">
                <tr className="border-t-2 border-accent/30">
                  <td className="p-2 font-semibold" colSpan={4}>{tr("total")}</td>
                  <td className="p-2 text-right text-lg font-bold tabular-nums">{num(formatHMS(totalSec))}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setRows((rs) => [...rs, newRow()])}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium hover:border-accent",
                lang === "bn" && "font-bn",
              )}
            >
              <Plus size={12} /> {tr("addRow")}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium hover:border-accent",
                lang === "bn" && "font-bn",
              )}
            >
              <Download size={12} /> {tr("exportCsv")}
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <NumField label={tr("rate")} value={rate} onChange={setRate} lang={lang} useBn={useBn} />
            <div className="rounded-xl border border-border bg-card p-4">
              <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>{tr("wage")}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">
                ৳{num(wage.toLocaleString("en-US", { maximumFractionDigits: 2 }))}
              </p>
              <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {num((totalSec / 3600).toFixed(2))} {lang === "en" ? "hours" : "ঘণ্টা"} × ৳{num(rate)}/{lang === "en" ? "hr" : "ঘ"}
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Tab 4: Convert */}
        <TabsContent value="convert" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <NumField label={tr("value")} value={convVal} onChange={setConvVal} lang={lang} useBn={useBn} />
            <div className="space-y-1.5">
              <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("unit")}</label>
              <select
                value={convUnit}
                onChange={(e) => setConvUnit(e.target.value as TimeUnit)}
                className="w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-base outline-none focus:border-accent focus:bg-background"
              >
                <option value="seconds">{tr("seconds")}</option>
                <option value="minutes">{tr("minutes")}</option>
                <option value="hours">{tr("hours")}</option>
                <option value="days">{tr("days")}</option>
                <option value="weeks">{tr("weeks")}</option>
              </select>
            </div>
          </div>

          {conv && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="grid gap-3 sm:grid-cols-2 md:grid-cols-5">
              {(Object.keys(conv) as TimeUnit[]).map((u) => (
                <Stat
                  key={u}
                  label={tr(u as keyof typeof T)}
                  value={num(conv[u].toLocaleString("en-US", { maximumFractionDigits: 4 }))}
                />
              ))}
            </motion.div>
          )}

          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            <p className={cn(lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Reference: 1 day = 24 hours = 1,440 minutes = 86,400 seconds. 1 week = 7 days = 604,800 seconds."
                : "তথ্যসূত্র: ১ দিন = ২৪ ঘণ্টা = ১,৪৪০ মিনিট = ৮৬,৪০০ সেকেন্ড। ১ সপ্তাহ = ৭ দিন = ৬,০৪,৮০০ সেকেন্ড।"}
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "How time calculation works" : "টাইম গণনা কীভাবে হয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "We convert each time to seconds since midnight, then perform arithmetic. 12-hour format (with AM/PM) is the everyday Bangladeshi convention; 24-hour format is used for schedules, transit, and military time. Toggle the format with the switch above."
              : "প্রতিটি সময়কে রাত ১২টা থেকে সেকেন্ডে রূপান্তর করে গণনা করা হয়। ১২-ঘণ্টা ফরম্যাট (AM/PM) দৈনন্দিন বাংলাদেশী রীতি; ২৪-ঘণ্টা ফরম্যাট সময়সূচী, পরিবহন ও সামরিক ব্যবহারে। উপরের সুইচ থেকে ফরম্যাট বদলান।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Cross-midnight calculations" : "ক্রস-মিডনাইট গণনা"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "When end time is earlier than start time (e.g., a night shift from 11:30 PM to 2:30 AM), the calculator assumes the end is on the next day and adds 24 hours, giving 3 hours total. For shifts spanning multiple days, fill in both dates."
              : "শেষ সময় শুরুর চেয়ে কম হলে (যেমন রাত ১১:৩০ → ভোর ২:৩০ পর্যন্ত নাইট শিফট) ক্যালকুলেটর পরের দিন ধরে ২৪ ঘণ্টা যোগ করে = মোট ৩ ঘণ্টা। একাধিক দিনের শিফটে দুটি তারিখ পূরণ করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Use cases" : "ব্যবহারের ক্ষেত্র"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Shift work — calculate hours for garment, RMG, hospital, restaurant staff" : "শিফট ওয়ার্ক — পোশাক, হাসপাতাল, রেস্টুরেন্ট কর্মীদের ঘণ্টা"}</li>
            <li>{lang === "en" ? "Freelance — track billable hours per task or per client" : "ফ্রিল্যান্স — প্রতিটি কাজ বা ক্লায়েন্টের ঘণ্টা ট্র্যাক"}</li>
            <li>{lang === "en" ? "Study time — sum up daily study sessions for exam prep" : "অধ্যয়ন সময় — পরীক্ষার প্রস্তুতির জন্য দৈনিক স্টাডি সেশন যোগফল"}</li>
            <li>{lang === "en" ? "Cooking, workouts, meditation — any timed activity" : "রান্না, ব্যায়াম, মেডিটেশন — যেকোনো সময়ভিত্তিক কার্যক্রম"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Time unit reference" : "সময়ের একক তথ্যসূত্র"} lang={lang}>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Seconds" : "সেকেন্ড"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Minutes" : "মিনিট"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Hours" : "ঘণ্টা"}</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                <tr className="border-t border-border"><td className="p-2">1 {lang === "en" ? "minute" : "মিনিট"}</td><td className="p-2 text-right">60</td><td className="p-2 text-right">1</td><td className="p-2 text-right">0.0167</td></tr>
                <tr className="border-t border-border"><td className="p-2">1 {lang === "en" ? "hour" : "ঘণ্টা"}</td><td className="p-2 text-right">3,600</td><td className="p-2 text-right">60</td><td className="p-2 text-right">1</td></tr>
                <tr className="border-t border-border"><td className="p-2">1 {lang === "en" ? "day" : "দিন"}</td><td className="p-2 text-right">86,400</td><td className="p-2 text-right">1,440</td><td className="p-2 text-right">24</td></tr>
                <tr className="border-t border-border"><td className="p-2">1 {lang === "en" ? "week" : "সপ্তাহ"}</td><td className="p-2 text-right">604,800</td><td className="p-2 text-right">10,080</td><td className="p-2 text-right">168</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/date-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Date" : "তারিখ"}</Link>
            <Link to="/year-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Year" : "বছর"}</Link>
            <Link to="/age-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Age" : "বয়স"}</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function parseTimeInput(s: string): TimeParts | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  // Accept "HH:MM", "HH:MM:SS", with optional AM/PM
  const m = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)?$/i.exec(trimmed);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] ? parseInt(m[3], 10) : 0;
  const ap = m[4]?.toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  if (h > 23 || min > 59 || sec > 59) return null;
  return { h, m: min, s: sec };
}

function intOr(s: string, fallback: number): number {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : fallback;
}

function updateRow(i: number, patch: Partial<SheetRow>, rows: SheetRow[], setRows: (r: SheetRow[]) => void) {
  setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function NumField({
  label, value, onChange, lang, useBn,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang; useBn: boolean }) {
  const display = useBn ? toBengaliNumerals(value) : value;
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={(e) =>
          onChange(
            e.target.value
              .replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)))
              .replace(/[^0-9.]/g, ""),
          )
        }
        className="w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-base tabular-nums outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function TimeField({
  label, value, onChange, lang,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang }) {
  return (
    <div className="space-y-1.5">
      <label className={cn("flex items-center gap-1.5 text-sm font-medium", lang === "bn" && "font-bn")}>
        <Clock size={12} className="text-muted-foreground" /> {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="HH:MM or HH:MM AM"
        className="w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-base tabular-nums outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function DateInput({
  label, value, onChange, lang,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang }) {
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-base outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
